import React, { useMemo, useState } from 'react';
import { CalendarClockIcon, PhoneIcon, ReceiptTextIcon } from 'lucide-react';
import { ListPageLayout, type FilterChip } from '../components/lists/ListPageLayout';
import { LogActivityModal } from '../components/activity/LogActivityModal';
import { pastClientDueState, pastClients } from '../data/pastClients';
import { useToast } from '../contexts/ToastContext';
import { useActivityPlan } from '../contexts/ActivityPlanContext';
import { actualForPeriod, targetForPeriod } from '../utils/activityProgress';
import type { PastClient } from '../types/activity';
const filters: FilterChip[] = [{
  id: 'all',
  label: 'All clients',
  count: pastClients.length
}, {
  id: 'overdue',
  label: 'Quarterly touch due',
  count: pastClients.filter(client => pastClientDueState(client).overdue).length
}, {
  id: 'planning',
  label: 'Planning review',
  count: pastClients.filter(client => client.planningCallDue).length
}, {
  id: 'escrow',
  label: 'Escrow analysis',
  count: pastClients.filter(client => client.escrowAnalysisDue).length
}];
function matchesFilter(client: PastClient, filter: string): boolean {
  if (filter === 'overdue') return pastClientDueState(client).overdue;
  if (filter === 'planning') return client.planningCallDue;
  if (filter === 'escrow') return client.escrowAnalysisDue;
  return true;
}
export function PastClientsPage() {
  const {
    showToast
  } = useToast();
  const {
    activeGoals,
    logs
  } = useActivityPlan();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [logTarget, setLogTarget] = useState<PastClient | null>(null);
  const goal = activeGoals.find(item => item.category === 'past-client-call');
  const weekActual = actualForPeriod(logs, 'past-client-call', 'week');
  const weekTarget = goal ? targetForPeriod(goal, 'week') : 0;
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pastClients.filter(client => {
      if (!matchesFilter(client, activeFilter)) return false;
      if (!term) return true;
      return client.name.toLowerCase().includes(term) || client.program.toLowerCase().includes(term);
    });
  }, [search, activeFilter]);
  return <>
      <ListPageLayout title="Past Clients" subtitle="Recurring quarterly touches plus mortgage-planning and escrow-analysis reviews." banner={weekTarget > 0 ? <div className="rounded-xl border border-success-200 bg-success-50 px-4 py-3" data-id="element-775" data-mp="32ulgj96pi5j7">
              <p className="text-sm font-semibold text-success-700" data-id="element-776" data-mp="2p3fbyyoklhpg">
                {weekActual} of {weekTarget} past-client touches logged this week
              </p>
              <p className="mt-0.5 text-xs text-success-600" data-id="element-777" data-mp="2u2ruj78es2m8">
                Anyone past 90 days appears as a triggered prompt on your Perfect
                Day.
              </p>
            </div> : undefined} searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search past clients by name or program" filters={filters} activeFilter={activeFilter} onFilterChange={setActiveFilter} loading={false} isEmpty={visible.length === 0} emptyTitle="No past clients match" emptyMessage="Try a different filter or clear your search." resultLabel={`${visible.length} of ${pastClients.length} past clients`} data-id="element-774" data-mp="3ct1b5m4xzwxg">
        <ul className="divide-y divide-navy-100" data-id="element-778" data-mp="2fft7dogl3udb">
          {visible.map(client => {
          const due = pastClientDueState(client);
          return <li key={client.id} className="flex flex-wrap items-start gap-3 px-4 py-4 hover:bg-navy-50/40" data-id="element-779" data-mp="2t6pzzfaigbkd">
                <div className="min-w-0 flex-1" data-id="element-780" data-mp="334vzjq8uh5m2">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-781" data-mp="335nhvzdfl6ue">
                    <p className="text-sm font-semibold text-navy-900" data-id="element-782" data-mp="2y7dptxn06md0">
                      {client.name}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${due.classes}`} data-id="element-783" data-mp="vhks6yx5uv3b">
                      {due.label}
                    </span>
                    {client.planningCallDue ? <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brand-700 ring-1 ring-brand-200" data-id="element-784" data-mp="1aae64fwsb35v">
                        Planning review
                      </span> : null}
                    {client.escrowAnalysisDue ? <span className="inline-flex items-center rounded-full bg-warn-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-warn-700 ring-1 ring-warn-200" data-id="element-785" data-mp="ribz7w9y318t">
                        Escrow analysis
                      </span> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-navy-600" data-id="element-786" data-mp="3nfj9r5f3jsl1">
                    {client.closedLabel} · {client.program} · {client.rate}
                  </p>
                  <p className="mt-1 text-xs text-navy-500" data-id="element-787" data-mp="3iij3atec4nte">
                    Last quarterly touch:{' '}
                    {client.quarterlyTouchLabel ?? 'none logged'} ·{' '}
                    {client.equityNote}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-788" data-mp="33stymo71a1od">
                  <a href={`tel:${client.phone}`} aria-label={`Call ${client.name}`} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50" data-id="element-789" data-mp="2zom6si5a45lx">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-790" data-mp="szhe9b9p8xrh" />
                  </a>
                  {client.planningCallDue ? <button type="button" onClick={() => showToast(`Mortgage-planning review requested for ${client.name}.`)} className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-791" data-mp="o0xm2t4iipqo">
                      <CalendarClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-792" data-mp="316kvp6zah7qy" />
                      Planning call
                    </button> : null}
                  {client.escrowAnalysisDue ? <button type="button" onClick={() => showToast(`Escrow analysis pulled for ${client.name}.`)} className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-793" data-mp="2r7vdte7e7nap">
                      <ReceiptTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-794" data-mp="2tobnm1him7p" />
                      Escrow
                    </button> : null}
                  <button type="button" onClick={() => setLogTarget(client)} className="inline-flex h-10 items-center rounded-lg bg-navy-800 px-3 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-795" data-mp="bve0ql7w992b">
                    Log touch
                  </button>
                </div>
              </li>;
        })}
        </ul>
      </ListPageLayout>

      <LogActivityModal open={logTarget !== null} onClose={() => setLogTarget(null)} defaultCategory="past-client-call" lockCategory contact={logTarget ? {
      id: logTarget.id,
      name: logTarget.name,
      detail: `${logTarget.closedLabel} · ${logTarget.program}`,
      type: 'past-client'
    } : undefined} lockContact data-id="element-796" data-mp="3p37lv5t8q1ma" />
    </>;
}